/**
 * Decide whether to mount the mobile UI shell.
 *
 * Priority:
 * 1. ?ui=mobile|desktop|auto in URL
 * 2. REACT_APP_TARGET=mobile|desktop — forced at build time (Capacitor)
 * 3. Runtime heuristics (UA, screen size, touch) — works even with Xiaomi "PC mode"
 */

const UI_OVERRIDE_KEY = 'selfpass_ui_override';

const readQueryUiOverride = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const ui = params.get('ui');
        if (ui === 'auto') {
            sessionStorage.removeItem(UI_OVERRIDE_KEY);
            return 'auto';
        }
        if (ui === 'mobile' || ui === 'desktop') {
            sessionStorage.setItem(UI_OVERRIDE_KEY, ui);
            return ui;
        }
    } catch (error) {
        // ignore
    }
    return null;
};

const readStoredUiOverride = () => {
    try {
        const stored = sessionStorage.getItem(UI_OVERRIDE_KEY);
        if (stored === 'mobile' || stored === 'desktop') {
            return stored;
        }
    } catch (error) {
        // ignore
    }
    return null;
};

const clearStoredUiOverride = () => {
    try {
        sessionStorage.removeItem(UI_OVERRIDE_KEY);
    } catch (error) {
        // ignore
    }
};

export const getForcedTarget = () => {
    const target = process.env.REACT_APP_TARGET;
    if (target === 'mobile' || target === 'desktop') {
        return target;
    }
    return null;
};

const getScreenShortestSide = () => {
    const width = window.screen?.width || 0;
    const height = window.screen?.height || 0;
    if (!width || !height) {
        return 0;
    }
    return Math.min(width, height);
};

/**
 * Physical phone/tablet screen — stays true even when browser enables
 * "Desktop site" / PC mode and inflates layout viewport (common on Xiaomi).
 */
export const isHandheldScreen = () => {
    const shortest = getScreenShortestSide();
    if (shortest > 0 && shortest <= 550) {
        return true;
    }

    // Larger phones / small tablets with touch
    if (shortest > 0 && shortest <= 850 && (navigator.maxTouchPoints || 0) > 0) {
        return true;
    }

    return false;
};

const isMobileUserAgent = () => {
    const ua = navigator.userAgent || navigator.vendor || '';
    // Android/iOS even without the word "Mobile" (some desktop-mode UAs still keep Android)
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet|Silk|Kindle/i.test(ua)) {
        return true;
    }
    // Xiaomi / Chinese OEM browsers
    if (/MiuiBrowser|XiaoMi|Mi-Lite|HeyTapBrowser|HuaweiBrowser|VivoBrowser|OppoBrowser|SamsungBrowser|UCBrowser|Quark/i.test(ua)) {
        return true;
    }
    return false;
};

/**
 * True when the visitor is on a phone/tablet browser.
 */
export const detectMobileDevice = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    if (window.Capacitor?.isNativePlatform?.() || window.cordova) {
        return true;
    }

    if (navigator.userAgentData?.mobile === true) {
        return true;
    }

    if (isMobileUserAgent()) {
        return true;
    }

    // iPadOS 13+ often reports as Macintosh
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
        return true;
    }

    // Prefer screen metrics over layout viewport (desktop-site mode widens innerWidth)
    if (isHandheldScreen()) {
        return true;
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
        return true;
    }

    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (coarsePointer && (navigator.maxTouchPoints || 0) > 0 && getScreenShortestSide() <= 900) {
        return true;
    }

    return false;
};

export const shouldUseMobileApp = () => {
    if (typeof window !== 'undefined') {
        const queryOverride = readQueryUiOverride();
        if (queryOverride === 'mobile') return true;
        if (queryOverride === 'desktop') return false;
        // 'auto' falls through after clearing storage

        // Stale ?ui=desktop must not trap real phones (Xiaomi PC mode testing, etc.)
        if (isHandheldScreen() || isMobileUserAgent() || navigator.userAgentData?.mobile === true) {
            const stored = readStoredUiOverride();
            if (stored === 'desktop') {
                clearStoredUiOverride();
            } else if (stored === 'mobile') {
                return true;
            }
            return detectMobileDevice();
        }

        const storedOverride = readStoredUiOverride();
        if (storedOverride === 'mobile') return true;
        if (storedOverride === 'desktop') return false;
    }

    const forced = getForcedTarget();
    if (forced === 'mobile') return true;
    if (forced === 'desktop') return false;

    return detectMobileDevice();
};
